import {
  attemptBootstrapRecovery,
  attemptManualBootstrapRecovery,
  clearBootstrapRecoveryGuard,
  createBootstrapCorrelationId,
} from "./bootstrapRecovery";
import { isXeroAccountLinkingFixtureRouteFamily } from "./pages/dev/xeroAccountLinkingFixtureRoute";

const container = document.getElementById("root");

if (!container) {
  throw new Error("Root container not found");
}

const isStandaloneXeroFixture =
  import.meta.env.DEV &&
  isXeroAccountLinkingFixtureRouteFamily({
    isDev: true,
    pathname: window.location.pathname,
  });

const BOOTSTRAP_FAILURE_MESSAGE = "Application entry failed to load";
const BOOTSTRAP_FAILURE_ERROR_CODE = "APP_BOOTSTRAP_FAILURE";
type BootstrapFailureCategory =
  | "auth_init"
  | "dynamic_import"
  | "other"
  | "reference_error"
  | "syntax_error"
  | "type_error";

const reportBootstrapFailure = async (
  correlationId: string,
  failureCategory: BootstrapFailureCategory,
): Promise<void> => {
  const safeError = new Error(BOOTSTRAP_FAILURE_MESSAGE);

  try {
    const { default: FrontendLoggerFactory } =
      await import("./utils/logging/FrontendLoggerFactory");

    FrontendLoggerFactory.getLogger()
      .withCorrelationId(correlationId)
      .withComponent("AppBootstrap")
      .error(BOOTSTRAP_FAILURE_MESSAGE, safeError, {
        correlationId,
        errorCode: BOOTSTRAP_FAILURE_ERROR_CODE,
        failureCategory,
        operation: "app_bootstrap_failure",
      });
  } catch {
    // The shared logger is part of the application graph and may be unavailable
    // for the same reason as the app entry. Keep one safe browser-level signal.
    console.error(
      `[APP_BOOTSTRAP] ${BOOTSTRAP_FAILURE_MESSAGE} [${BOOTSTRAP_FAILURE_ERROR_CODE}] (${correlationId}) [${failureCategory}]`,
    );
  }
};

const renderBootstrapFailure = (
  failureCategory: BootstrapFailureCategory,
): void => {
  const correlationId = createBootstrapCorrelationId();
  const main = document.createElement("main");
  const content = document.createElement("section");
  const heading = document.createElement("h1");
  const explanation = document.createElement("p");
  const reloadButton = document.createElement("button");
  const reference = document.createElement("p");

  heading.textContent = "HelloCashflow couldn’t load";
  explanation.textContent =
    "Reload the page. If it still doesn’t work, open HelloCashflow in a private window or contact support.";
  reloadButton.type = "button";
  reloadButton.textContent = "Reload HelloCashflow";
  reloadButton.addEventListener("click", () => {
    clearBootstrapRecoveryGuard();
    attemptManualBootstrapRecovery();
  });
  reference.textContent = `Reference: ${correlationId} · Error code: ${BOOTSTRAP_FAILURE_ERROR_CODE}`;

  main.style.cssText =
    "box-sizing:border-box;display:grid;min-height:100vh;place-items:center;padding:24px;font-family:system-ui,-apple-system,sans-serif;background:Canvas;color:CanvasText";
  content.style.cssText = "max-width:560px;text-align:center";
  heading.style.cssText = "margin:0 0 16px";
  explanation.style.cssText = "margin:0 0 24px;line-height:1.5";
  reference.style.cssText = "margin:0 0 24px;line-height:1.5";
  reloadButton.style.cssText =
    "border:1px solid ButtonBorder;border-radius:4px;padding:10px 18px;font:inherit;cursor:pointer;background:ButtonFace;color:ButtonText";

  content.append(heading, explanation, reference, reloadButton);
  main.append(content);
  container.replaceChildren(main);
  reloadButton.focus();

  void reportBootstrapFailure(correlationId, failureCategory);
};

let bootstrapFailureHandled = false;
const handleBootstrapFailure = (
  failureCategory: BootstrapFailureCategory = "other",
): void => {
  if (bootstrapFailureHandled) return;
  bootstrapFailureHandled = true;
  if (attemptBootstrapRecovery()) return;
  renderBootstrapFailure(failureCategory);
};

const handlePreloadError = (event: Event): void => {
  event.preventDefault();
  handleBootstrapFailure("dynamic_import");
};

window.addEventListener("vite:preloadError", handlePreloadError);

void (
  isStandaloneXeroFixture
    ? import("./pages/dev/renderXeroAccountLinkingFixture").then(
        ({ renderXeroAccountLinkingFixture }) =>
          renderXeroAccountLinkingFixture(container),
      )
    : import("./framework/renderApp").then(({ renderApp }) =>
        renderApp(container),
      )
)
  .then(() => {
    clearBootstrapRecoveryGuard();
    window.removeEventListener("vite:preloadError", handlePreloadError);
  })
  .catch((error: unknown) => {
    const message = error instanceof Error ? error.message : "";
    const classification = /dynamically imported module|chunkload/iu.test(
      message,
    )
      ? "dynamic_import"
      : /firebase|auth/iu.test(message)
        ? "auth_init"
        : error instanceof ReferenceError
          ? "reference_error"
          : error instanceof TypeError
            ? "type_error"
            : error instanceof SyntaxError
              ? "syntax_error"
              : "other";
    handleBootstrapFailure(classification);
  });
